import { useEffect, useMemo, useState } from 'react';
import EditableRecordTable from '../components/EditableRecordTable.jsx';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import DashboardFilterPanel from '../components/DashboardFilterPanel.jsx';
import JoinedSummaryCell from '../components/JoinedSummaryCell.jsx';
import {
  createDeliverableManagement,
  deleteDeliverableManagement,
  getDeliverableLinkTargets,
  getDeliverableManagements,
  updateDeliverableManagement,
} from '../api/dashboardApi.js';
import { exportToExcel } from '../utils/exportUtils.js';
import ExportConfirmPopup from '../components/ExportConfirmPopup.jsx';
import { canEdit } from '../../../auth/permissions.js';
import { isNoDeliverableLabel, normalizeProductName } from '../utils/converters.js';
import { buildConflictMessage } from '../utils/conflictMessage.js';
import { DELIVERABLE_MESSAGES, NO_DELIVERABLE_LABEL } from '../constants/messages.js';
import '../styles/deliverable-set.css';
import {
  flattenRow,
  actorCheckKey,
  buildSelectOptions,
  extractActorCheckColumns,
  LINK_LEVELS,
  buildLinkOptions,
  buildSchedulesByActor,
  clearInvalidLinkSelections,
  clearUnavailableActorChecks,
  resolveProgressScheduleIds,
  resolveScopeProgressScheduleIds,
  distinctCoordinateValues,
} from '../utils/deliverableConverters.js';

// 進捗管理から導出した項目の一覧セル。1つの成果物が複数の座標に紐付くと
// バックエンドがカンマ区切りで返すため、列幅に収まるよう先頭だけ出して残りは件数にする。
// 件数は結合文字列ではなく linked_coordinates 配列から数える（名称が区切り文字を
// 含んでも誤認しない）。ポップアップは全件見せる場所なので畳まない。
const derivedCellRenderer = (targetKey) => (value, row, options) => (
  options?.readOnly
    ? /* istanbul ignore next -- 参照表示の対象は必ず紐付きのある成果物 */ (value || '—')
    : <JoinedSummaryCell items={distinctCoordinateValues(row, targetKey)} />
);

// OEM×対地・カテゴリ・大中小項目・Phase は「どの進捗管理に紐付けるか」を決める
// カスケード選択。選択肢は進捗管理に実在する組み合わせだけに絞られる。
// OEM×対地 以外は必須にしない。カテゴリ〜Phase を空にして進捗管理と紐付けない状態も
// 正規の状態であり、そのとき小項目などは表示されなくなる。
// ただし座標を選んだまま領域チェックを全て外すとリンクが作られず選択が消えるので、
// その組み合わせだけは保存時に弾く（handlePopupSave）。
const LINK_COLUMNS = [
  // OEM×対地 は成果物自身が持つ項目なので常に単一。畳む必要がない。
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', width: '180px', required: true },
  { key: 'categoryName', label: 'カテゴリ', type: 'select', width: '120px', renderDisplay: derivedCellRenderer('category_name') },
  { key: 'largeCategoryName', label: '大項目', type: 'select', width: '200px', renderDisplay: derivedCellRenderer('large_category_name') },
  { key: 'middleCategoryName', label: '中項目', type: 'select', width: '220px', renderDisplay: derivedCellRenderer('middle_category_name') },
  { key: 'smallCategoryName', label: '小項目', type: 'select', width: '250px', renderDisplay: derivedCellRenderer('small_category') },
  { key: 'phaseName', label: 'Phase', type: 'select', width: '120px', renderDisplay: derivedCellRenderer('phase_name') },
];

// 成果物そのものが保持する項目
// 成果物名「なし」は成果物が無いことを表す印なので、URL は任意項目になる
const PRODUCT_COLUMNS = [
  {
    key: 'product',
    label: '成果物名',
    type: 'text',
    width: '260px',
    required: true,
    // 「成果物なし」はフラグで管理するため、手入力ではなくボタンで切り替える
    renderEditor: ({ row, value, update, updateRow }) => {
      const isNone = Boolean(row?.noDeliverable);
      return (
        <div className="deliverable-set__product">
          <input
            className="db-input-control"
            value={value || ''}
            onChange={(e) => update(e.target.value)}
            placeholder="成果物名"
            readOnly={isNone}
          />
          <button
            type="button"
            className="deliverable-set__none"
            onClick={() => updateRow(isNone
              ? { product: '', noDeliverable: false }
              : { product: NO_DELIVERABLE_LABEL, noDeliverable: true })}
          >
            {isNone ? '「成果物なし」を解除' : '成果物なし'}
          </button>
        </div>
      );
    },
  },
  {
    key: 'deliverableUrl',
    label: '成果物保管URL',
    type: 'url',
    width: '300px',
    required: ({ row }) => !row?.noDeliverable,
  },
];

const AUDIT_COLUMNS = [
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
];

// 表示順は進捗管理タブと揃える（OEM×対地 → カテゴリ〜Phase → 成果物）
const STATIC_COLUMNS = [...LINK_COLUMNS, ...PRODUCT_COLUMNS];

const LEVEL_INDEX_BY_KEY = Object.fromEntries(
  LINK_LEVELS.map((level, index) => [level.rowKey, index]),
);

// OEM×対地 は成果物自身の項目なので、残りのカスケード項目を「座標の指定」とみなす。
// 1つでも選んでいれば、進捗管理と紐付ける意図があると判断する。
const COORDINATE_ROW_KEYS = LINK_LEVELS.slice(1).map((level) => level.rowKey);

function hasCoordinateSelection(row) {
  return COORDINATE_ROW_KEYS.some((key) => row[key]);
}

// フィルターの選択肢は編集対象の座標だけでなく、紐付く座標すべてから集める。
// matches で上位フィルターに合う座標だけに絞れる（大項目→中項目の連動用）。
function buildCoordinateOptions(rows, targetKey, matches = () => true) {
  const values = new Set(['']);
  rows.forEach((row) => {
    /* istanbul ignore next -- flattenRow が linked_coordinates を必ず配列に整える */
    (row.linked_coordinates || []).forEach((coordinate) => {
      
      const value = coordinate?.[targetKey] || '';
      if (value && matches(coordinate)) values.add(value);
    });
  });
  return Array.from(values).sort((a, b) => a.localeCompare(b, 'ja'));
}

export default function DashboardDeliverableManagementPage() {
  const [rawRows, setRawRows] = useState([]);
  const [linkTargets, setLinkTargets] = useState([]);
  const [oemRegionFilter, setOemRegionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [largeCategoryFilter, setLargeCategoryFilter] = useState('');
  const [middleCategoryFilter, setMiddleCategoryFilter] = useState('');
  const [actorCheckFilter, setActorCheckFilter] = useState('');
  const [keywordFilter, setKeywordFilter] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [addSignal, setAddSignal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError('');
        const [deliverableData, targets] = await Promise.all([
          getDeliverableManagements(),
          getDeliverableLinkTargets(),
        ]);
        if (!active) return;
        setRawRows(deliverableData);
        setLinkTargets(targets);
      } catch (err) {
        if (!active) return;
        setError('成果物管理データの取得に失敗しました');
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, []);

  // 領域チェック列を動的に生成（進捗管理の領域を反映した表示専用の列）
  const actorCheckCols = useMemo(() => extractActorCheckColumns(rawRows), [rawRows]);

  const actorNames = useMemo(() => actorCheckCols.map((ac) => ac.actorName), [actorCheckCols]);

  // OEM×対地 のラベル → ID。選択肢と同じ linkTargets から作る
  const oemRegionLookup = useMemo(() => {
    const map = new Map();
    linkTargets.forEach((target) => {
      if (!map.has(target.oem_region)) {
        map.set(target.oem_region, { oemId: target.oem_id, regionId: target.region_id });
      }
    });
    return map;
  }, [linkTargets]);

  const baseColumns = useMemo(() => [
    ...STATIC_COLUMNS,
    ...actorCheckCols.map((ac) => ({
      key: ac.key,
      label: ac.actorName,
      type: 'checkbox',
      width: '120px',
    })),
    ...AUDIT_COLUMNS,
  ], [actorCheckCols]);

  // 行をフラット化
  const flatRows = useMemo(() => rawRows.map(flattenRow), [rawRows]);

  const selectColumnKeys = useMemo(
    () => baseColumns.filter((col) => col.type === 'select').map((col) => col.key),
    [baseColumns],
  );

  const selectOptions = useMemo(
    () => buildSelectOptions(flatRows, selectColumnKeys),
    [flatRows, selectColumnKeys],
  );

  const categoryFilterOptions = useMemo(
    () => buildCoordinateOptions(flatRows, 'category_name'),
    [flatRows],
  );

  const largeCategoryFilterOptions = useMemo(
    () => buildCoordinateOptions(flatRows, 'large_category_name'),
    [flatRows],
  );

  // 中項目の候補は選択中の大項目配下だけに絞る（進捗管理タブと同じ連動）
  const middleCategoryFilterOptions = useMemo(
    () => buildCoordinateOptions(
      flatRows,
      'middle_category_name',
      (coordinate) => !largeCategoryFilter
        || coordinate.large_category_name === largeCategoryFilter,
    ),
    [flatRows, largeCategoryFilter],
  );

  const actorCheckFilterOptions = useMemo(() => ['', ...actorNames], [actorNames]);

  // フィルタ適用
  // カスケード列は編集対象の座標しか持たないため、絞り込み・検索は紐付く座標
  // すべて（linked_coordinates）を対象にする。どの座標で引いても行が見つかる。
  const filteredRows = useMemo(() => {
    const keyword = keywordFilter.trim().toLowerCase();
    // カスケード項目は「1つの座標が全条件を満たすか」で判定する。
    // 別々の座標にバラバラに一致しただけの行を拾わないため。
    const matchesCoordinates = (row) => {
      if (!categoryFilter && !largeCategoryFilter && !middleCategoryFilter) return true;
      /* istanbul ignore next -- 同上 */
      return (row.linked_coordinates || []).some((coordinate) => (
        (!categoryFilter || coordinate.category_name === categoryFilter)
        && (!largeCategoryFilter || coordinate.large_category_name === largeCategoryFilter)
        && (!middleCategoryFilter || coordinate.middle_category_name === middleCategoryFilter)
      ));
    };

    return flatRows.filter((row) => {
      if (oemRegionFilter && row.oemRegion !== oemRegionFilter) return false;
      if (!matchesCoordinates(row)) return false;
      if (actorCheckFilter && !row[actorCheckKey(actorCheckFilter)]) return false;
      if (keyword) {
        // 専用フィルターを持つ項目は対象にせず、残りの列だけを検索する
        const searchable = [
          row.product,
          ...distinctCoordinateValues(row, 'small_category'),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(keyword)) return false;
      }
      return true;
    });
  }, [
    actorCheckFilter,
    categoryFilter,
    flatRows,
    keywordFilter,
    largeCategoryFilter,
    middleCategoryFilter,
    oemRegionFilter,
  ]);

  const columns = useMemo(
    () => baseColumns.map((col) => {
      const levelIndex = LEVEL_INDEX_BY_KEY[col.key];
      if (levelIndex !== undefined) {
        return {
          ...col,
          // 上位の選択で絞った、進捗管理に実在する値だけを出す。
          // 既存行が複数の進捗管理に紐付いてカンマ区切りになっている場合は、
          // 選択中の値が消えないよう先頭に足しておく。
          getOptions: ({ row }) => {
            const options = buildLinkOptions(linkTargets, row, levelIndex);
            const current = row?.[col.key] || '';
            if (current && !options.includes(current)) return ['', current, ...options];
            return ['', ...options];
          },
          // 上位を変えたら矛盾する下位と、選べなくなった領域チェックをクリアする
          onChange: ({ nextRow }) => clearUnavailableActorChecks(
            linkTargets,
            clearInvalidLinkSelections(linkTargets, nextRow, levelIndex),
            actorNames,
          ),
        };
      }
      if (col.type === 'checkbox') {
        // 選択中の小項目を持つ進捗管理がその領域に無ければチェックさせない
        return {
          ...col,
          getDisabled: ({ row }) => !buildSchedulesByActor(linkTargets, row).has(col.label),
          disabledHint: 'この小項目を持つ進捗管理が、この領域にありません',
        };
      }
      /* istanbul ignore if -- select 列（OEM×対地・カテゴリ〜Phase）はここまでに処理済み */
      if (col.type === 'select') {
        return { ...col, options: selectOptions[col.key] || [''] };
      }
      return col;
    }),
    [baseColumns, linkTargets, selectOptions, actorNames],
  );

  const filterFields = useMemo(() => [
    {
      key: 'oemRegion',
      label: 'OEM×対地',
      type: 'select',
      options: (/* istanbul ignore next -- buildSelectOptions の戻り値は引数 keys と同じキーを必ず持ち、参照側も同じ列集合を使う */ selectOptions.oemRegion || ['']).map((opt) => ({
        value: opt,
        label: opt || 'すべて',
      })),
    },
    {
      key: 'categoryName',
      label: 'カテゴリ',
      type: 'select',
      // 絞り込みは紐付く座標すべてを見るので、選択肢も全座標から集める
      options: categoryFilterOptions.map((opt) => ({
        value: opt,
        label: opt || 'すべて',
      })),
    },
    {
      key: 'largeCategoryName',
      label: '大項目',
      type: 'select',
      options: largeCategoryFilterOptions.map((opt) => ({
        value: opt,
        label: opt || 'すべて',
      })),
    },
    {
      key: 'middleCategoryName',
      label: '中項目',
      type: 'select',
      options: middleCategoryFilterOptions.map((opt) => ({
        value: opt,
        label: opt || 'すべて',
      })),
    },
    {
      key: 'actorCheck',
      label: '領域チェック',
      type: 'select',
      options: actorCheckFilterOptions.map((opt) => ({
        value: opt,
        label: opt || 'すべて',
      })),
    },
    {
      key: 'keyword',
      label: 'キーワード',
      type: 'text',
      className: 'pm-filter-item--keyword',
      placeholder: '小項目 / 成果物名',
    },
  ], [
    actorCheckFilterOptions,
    categoryFilterOptions,
    largeCategoryFilterOptions,
    middleCategoryFilterOptions,
    selectOptions.oemRegion,
  ]);

  const filterValues = {
    oemRegion: oemRegionFilter,
    categoryName: categoryFilter,
    largeCategoryName: largeCategoryFilter,
    middleCategoryName: middleCategoryFilter,
    actorCheck: actorCheckFilter,
    keyword: keywordFilter,
  };

  // 大項目を変えると中項目の候補が変わるので、選べなくなった値は落とす
  const handleLargeCategoryFilterChange = (value) => {
    setLargeCategoryFilter(value);
    const nextOptions = buildCoordinateOptions(
      flatRows,
      'middle_category_name',
      (coordinate) => !value || coordinate.large_category_name === value,
    );
    if (!nextOptions.includes(middleCategoryFilter)) {
      setMiddleCategoryFilter('');
    }
  };

  const handleFilterChange = (key, value) => {
    switch (key) {
      case 'oemRegion': setOemRegionFilter(value); break;
      case 'categoryName': setCategoryFilter(value); break;
      case 'largeCategoryName': handleLargeCategoryFilterChange(value); break;
      case 'middleCategoryName': setMiddleCategoryFilter(value); break;
      case 'actorCheck': setActorCheckFilter(value); break;
      case 'keyword': setKeywordFilter(value); break;
      /* istanbul ignore next -- key は既知のフィルタキーのみ渡されるため default は到達しない防御ガード */
      default: break;
    }
  };

  const clearFilters = () => {
    setOemRegionFilter('');
    setCategoryFilter('');
    setLargeCategoryFilter('');
    setMiddleCategoryFilter('');
    setActorCheckFilter('');
    setKeywordFilter('');
  };

  const handleExportConfirm = async (scope) => {
    const data = scope === 'filtered' ? filteredRows : flatRows;
    setExporting(true);
    try {
      await exportToExcel(data, baseColumns, '成果物管理');
    } finally {
      setExporting(false);
      setExportPopupOpen(false);
    }
  };

  const buildDeliverablePayload = (row) => {
    // OEM×対地 は成果物自身が持つ項目なので、座標が未確定でも単独で解決する
    const resolved = oemRegionLookup.get(row.oemRegion);
    const noDeliverable = Boolean(row.noDeliverable);
    const product = noDeliverable ? NO_DELIVERABLE_LABEL : normalizeProductName(row.product);
    // 「成果物なし」は URL が任意項目
    if (!resolved || !product || (!noDeliverable && !row.deliverableUrl)) {
      return null;
    }

    return {
      oem_id: resolved.oemId,
      region_id: resolved.regionId,
      product,
      no_deliverable: noDeliverable,
      deliverable_url: row.deliverableUrl || '',
      // チェックされた領域の進捗管理。全て外していれば空配列（＝リレーション解除）
      progress_schedule_ids: resolveProgressScheduleIds(linkTargets, row),
      // 今回触る範囲は「選択中の座標」＋「読み込み時の座標」。座標を選び直したときに
      // 移動元のリンクを外せる。それ以外の座標のリンクはサーバ側で温存される
      scope_progress_schedule_ids: resolveScopeProgressScheduleIds(linkTargets, row),
      updated_at_iso: row.updated_at_iso ?? null,
      // 進捗管理リンクの競合検出用（中間テーブルのバージョン）
      link_version: row.link_version ?? null,
    };
  };

  // 領域チェックが無いまま座標を保存しようとしたときの案内。
  // チェックできる領域があるなら「チェック忘れ」、無いなら「座標の選び直し」が次の一手。
  // 領域チェック列は対象領域だけの固定列なので、列に無い領域は数えない。
  const buildMissingActorCheckMessage = (row) => {
    const available = buildSchedulesByActor(linkTargets, row);
    return actorNames.some((name) => available.has(name))
      ? DELIVERABLE_MESSAGES.MISSING_ACTOR_CHECK
      : DELIVERABLE_MESSAGES.NO_LINKABLE_COORDINATE;
  };

  const handlePopupDelete = async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row.id) throw new Error('新規レコードは削除できません');
    await deleteDeliverableManagement(row.id, row.updated_at_iso ?? null);
    const deliverableData = await getDeliverableManagements();
    setRawRows(deliverableData);
  };

  const handlePopupSave = async (row) => {
    if (!row.noDeliverable && isNoDeliverableLabel(row.product)) {
      throw new Error(
        DELIVERABLE_MESSAGES.RESERVED_PRODUCT_NAME(normalizeProductName(row.product)),
      );
    }
    const payload = buildDeliverablePayload(row);
    if (!payload) {
      throw new Error(DELIVERABLE_MESSAGES.REQUIRED_FIELDS);
    }
    // 座標を選んでいても領域チェックが無ければ進捗管理へのリンクは1件も作られず、
    // カテゴリ〜Phase の選択は保存されずに消える。黙って消えると原因が分からないので、
    // 保存前に止めて選び直させる。
    if (payload.progress_schedule_ids.length === 0 && hasCoordinateSelection(row)) {
      throw new Error(buildMissingActorCheckMessage(row));
    }
    let savedId;
    try {
      if (row.id && !row.__isNew) {
        await updateDeliverableManagement(row.id, payload);
        savedId = row.id;
      } else {
        const created = await createDeliverableManagement(payload);
        savedId = created?.id;
      }
    } catch (e) {
      if (e.status === 409) {
        throw new Error(buildConflictMessage(e.message));
      }
      throw e;
    }
    const deliverableData = await getDeliverableManagements();
    setRawRows(deliverableData);
    if (savedId != null) {
      setHighlightedRowId(savedId);
      /* istanbul ignore next -- 5秒後にハイライトを解除するタイマー（jsdom で未発火） */
      setTimeout(() => setHighlightedRowId(null), 5000);
    }
  };

  const createEmptyRow = () => {
    const empty = {};
    baseColumns.forEach((col) => {
      empty[col.key] = col.type === 'checkbox' ? false : '';
    });
    empty.__isNew = true;
    return empty;
  };

  return (
    <>
    <DashboardInputLayout
      inputTabIndex={4}
      toolbar={
        !loading && !error ? (
          <DashboardInputToolbar
            countText={`${filteredRows.length} / ${flatRows.length} 件を表示`}
            exporting={exporting}
            onAdd={() => setAddSignal((prev) => prev + 1)}
            onExport={() => setExportPopupOpen(true)}
            onToggleFilters={() => setShowFilters((prev) => !prev)}
            showAdd
            showExport
            showFilters={showFilters}
            showFilterToggle
          />
        ) : null
      }
      filters={
        !loading && !error && showFilters ? (
          <DashboardFilterPanel
            fields={filterFields}
            onClear={clearFilters}
            onFieldChange={handleFilterChange}
            values={filterValues}
          />
        ) : null
      }
    >
      {loading && <div>読み込み中...</div>}
      {!loading && error && <div>{error}</div>}
      {!loading && !error && (
        <EditableRecordTable
          addButtonLabel="+ レコード追加"
          columns={columns}
          createEmptyRow={createEmptyRow}
          enableRowPopup
          externalAddSignal={addSignal}
          highlightedRowId={highlightedRowId}
          initialRows={filteredRows}
          onPopupDelete={handlePopupDelete}
          onPopupSave={handlePopupSave}
          readOnly={!canEdit('ダッシュボード')}
          rowPopupTitle="成果物管理レコード詳細"
          showAddButton={false}
        />
      )}
    </DashboardInputLayout>
    <ExportConfirmPopup
      open={exportPopupOpen}
      onClose={() => setExportPopupOpen(false)}
      onExport={handleExportConfirm}
      filteredCount={filteredRows.length}
      totalCount={flatRows.length}
      loading={exporting}
      sheetName="成果物管理"
      filterFields={filterFields}
      filterValues={filterValues}
    />
    </>
  );
}
